import { useMemo, useState, useEffect, useCallback } from "react";
import { translations, type TranslationKey, type Language } from "@/lib/translations";

// Caregiver language is stored separately and supports all 4 languages
const CAREGIVER_LANGUAGE_KEY = "ayuni_caregiver_language";

export type CaregiverLanguage = "he" | "en" | "ar" | "ru";

export function getCaregiverLanguage(): CaregiverLanguage {
  try {
    const stored = localStorage.getItem(CAREGIVER_LANGUAGE_KEY);
    if (stored === "en" || stored === "he" || stored === "ar" || stored === "ru") {
      return stored;
    }
  } catch (e) {
    console.error("Failed to load caregiver language:", e);
  }
  return "he"; // Default to Hebrew
}

export function setCaregiverLanguage(lang: CaregiverLanguage): void {
  try {
    localStorage.setItem(CAREGIVER_LANGUAGE_KEY, lang);
    // Dispatch event for reactive updates
    window.dispatchEvent(new CustomEvent('caregiverLanguageChanged', { 
      detail: { language: lang } 
    }));
  } catch (e) {
    console.error("Failed to save caregiver language:", e);
  }
}

export function useCaregiverTranslation() {
  const [lang, setLang] = useState<CaregiverLanguage>(getCaregiverLanguage);
  
  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent<{ language: CaregiverLanguage }>) => {
      setLang(event.detail.language);
    };
    
    window.addEventListener('caregiverLanguageChanged', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('caregiverLanguageChanged', handleLanguageChange as EventListener);
    };
  }, []);
  
  const setLanguage = useCallback((newLang: CaregiverLanguage) => {
    setCaregiverLanguage(newLang);
  }, []);
  
  const t = useMemo(() => {
    return (key: TranslationKey): string => {
      return translations[lang]?.[key] || translations.he[key] || key;
    };
  }, [lang]);
  
  // Hebrew and Arabic are RTL, English and Russian are LTR
  const isRTL = lang === "he" || lang === "ar";
  const dir = isRTL ? "rtl" : "ltr";
  
  return { t, lang, isRTL, dir, setLanguage };
}
